import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Clock, Phone, Send } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/translations";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "./Reveal";

export const Location = ({ content }) => {
  const { lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t("sent", lang));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: MapPin, label: t("address", lang), value: content.address },
    { icon: Clock, label: t("hours", lang), value: lang === "es" ? content.hours_es : content.hours_en },
    { icon: Phone, label: t("phone", lang), value: content.phone },
  ];

  return (
    <section id="visit" className="relative z-10 bg-[#0A0A0A] py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-[#FF5A00]">{t("visit_kicker", lang)}</span>
          <h2 className="font-display uppercase text-5xl md:text-7xl mt-3 leading-none">{t("visit_title", lang)}</h2>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-white/10 h-72 md:h-full min-h-[320px]">
              <iframe
                title="map"
                data-testid="location-map"
                src={content.map_embed}
                className="w-full h-full grayscale-[0.3] invert-[0.05]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="mt-8 space-y-5">
              {info.map((it, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FF5A00]/15 text-[#FF5A00]">
                    <it.icon size={18} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-neutral-500">{it.label}</p>
                    <p className="text-lg font-semibold">{it.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-white/10 bg-[#121212] p-7 md:p-9">
              <h3 className="font-display uppercase text-3xl">{t("contact_title", lang)}</h3>
              <form onSubmit={submit} className="mt-7 space-y-5" data-testid="contact-form">
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400">{t("name", lang)}</label>
                  <input
                    data-testid="contact-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full rounded-xl bg-[#0A0A0A] border border-white/10 px-4 py-3 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/30 focus:outline-none transition"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400">Email</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full rounded-xl bg-[#0A0A0A] border border-white/10 px-4 py-3 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/30 focus:outline-none transition"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-neutral-400">{t("message", lang)}</label>
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 w-full rounded-xl bg-[#0A0A0A] border border-white/10 px-4 py-3 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/30 focus:outline-none transition resize-none"
                  />
                </div>
                <button
                  data-testid="contact-submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF5A00] hover:bg-[#FF7A33] text-black font-bold uppercase tracking-wide py-4 transition-all hover:scale-[1.02] disabled:opacity-60"
                >
                  <Send size={16} /> {t("send", lang)}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
